import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { locationService } from '../services/locationService';
import { geocodingService } from '../services/geocodingService';
import { PlatformConfig, isMobile } from '../utils/platform';
import StaticMapDisplay from './StaticMapDisplay';

// Map component that uses Google Maps JavaScript API directly
function GoogleMap({
  location,
  currentLocation,
  draggable = false,
  onLocationChange,
  onAddressChange,
  width = '100%',
  height = '300px',
  zoom = 14,
  apiKey
}) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  // Determine which location to display
  const displayLocation = location || currentLocation;

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !displayLocation) return;

    const mapOptions = {
      center: {
        lat: displayLocation.coordinates.lat,
        lng: displayLocation.coordinates.lng
      },
      zoom: zoom,
      mapTypeId: 'roadmap',
      mapId: 'CHECKBOXR_MAP', // Required for Advanced Markers
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false
    };

    mapInstance.current = new window.google.maps.Map(mapRef.current, mapOptions);

    // Create advanced marker (replaces deprecated google.maps.Marker)
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position: {
        lat: displayLocation.coordinates.lat,
        lng: displayLocation.coordinates.lng
      },
      map: mapInstance.current,
      gmpDraggable: draggable,
      title: location ? 'List Location' : 'Current Location'
    });

    markerRef.current = marker;

    // Add drag listener for draggable pins
    if (draggable && onLocationChange) {
      marker.addListener('gmp-dragend', async (event) => {
        const lat = event.position.lat;
        const lng = event.position.lng;
        
        const newLocation = {
          coordinates: { lat, lng },
          source: 'manual',
          timestamp: new Date().toISOString()
        };
        
        onLocationChange(newLocation);
        
        // Lookup address in real-time
        if (onAddressChange && apiKey) {
          try {
            const address = await geocodingService.getAddressFromCoordinates(lat, lng, apiKey);
            onAddressChange(address);
          } catch (error) {
            console.warn('Failed to geocode new location:', error);
            onAddressChange(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          }
        }
      });
    }

    // Cleanup function
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, [displayLocation, draggable, onLocationChange, location]);

  // Final cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (mapInstance.current) {
        mapInstance.current = null;
      }
    };
  }, []);

  // Center map when location changes (marker position is handled in marker effect above)
  useEffect(() => {
    if (!mapInstance.current || !displayLocation) return;

    const newPosition = {
      lat: displayLocation.coordinates.lat,
      lng: displayLocation.coordinates.lng
    };
    
    mapInstance.current.setCenter(newPosition);
  }, [displayLocation]);

  if (!displayLocation) {
    return (
      <Box 
        sx={{ 
          width, 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No location data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      ref={mapRef} 
      sx={{ 
        width, 
        height,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 1
      }} 
    />
  );
}

// Loading component
function MapLoading({ width, height }) {
  return (
    <Box 
      sx={{ 
        width, 
        height, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 1,
        gap: 2
      }}
    >
      <CircularProgress size={32} />
      <Typography variant="body2" color="text.secondary">
        Loading map...
      </Typography>
    </Box>
  );
}

// Error component
function MapError({ width, height, error }) {
  return (
    <Box sx={{ width, height }}>
      <Alert severity="warning" sx={{ height: '100%' }}>
        <Typography variant="body2">
          Map unavailable: {error}
        </Typography>
      </Alert>
    </Box>
  );
}

// Main MapLocationDisplay component
// Automatically chooses between interactive maps (web) and static maps (mobile)
// Interactive maps require Google Maps API key with proper HTTP referrer restrictions:
// - checkboxr.com/* 
// - *.checkboxr.com/*
// Static maps use server-side proxy and don't require client-side API key
function MapLocationDisplay({
  location = null,
  currentLocation = null,
  draggable = false,
  onLocationChange,
  onAddressChange,  // New prop for address updates
  width = '100%',
  height = '300px',
  zoom = 14,
  apiKey,
  showWhenEmpty = true,  // New prop to control empty state display
  forceStatic = false  // Force static map even on web
}) {
  const [userLocation, setUserLocation] = useState(currentLocation);
  const [locationError, setLocationError] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  
  // Determine whether to use static maps
  const useStaticMaps = forceStatic || PlatformConfig.useStaticMaps;
  
  // On mobile or when forced, draggable maps are not supported with static maps
  const effectiveDraggable = useStaticMaps ? false : draggable;

  // Debug logging (only in development)

  // Get current location if not provided and no list location exists
  // Only when showWhenEmpty is true and in draggable mode (create/edit dialog)
  useEffect(() => {
    if (!location && !currentLocation && !userLocation && showWhenEmpty && effectiveDraggable) {
      setLoadingLocation(true);
      
      // Safety check for locationService
      if (!locationService || typeof locationService.getCurrentLocation !== 'function') {
        console.error('LocationService not available or getCurrentLocation method missing');
        setLocationError('Location service not available');
        setLoadingLocation(false);
        return;
      }
      
      locationService.getCurrentLocation()
        .then((loc) => {
          setUserLocation(loc);
          setLocationError(null);
        })
        .catch((error) => {
          console.warn('Could not get current location:', error);
          setLocationError(locationService.getErrorMessage ? locationService.getErrorMessage(error) : 'Location error');
        })
        .finally(() => {
          setLoadingLocation(false);
        });
    }
  }, [location, currentLocation, userLocation, showWhenEmpty, effectiveDraggable]);

  const handleLocationChange = useCallback((newLocation) => {
    if (onLocationChange) {
      onLocationChange(newLocation);
    }
  }, [onLocationChange]);

  // Don't show anything if no location and showWhenEmpty is false
  if (!showWhenEmpty && !location && !currentLocation && !userLocation) {
    return null;
  }

  // Show loading while getting current location
  if (loadingLocation) {
    return <MapLoading width={width} height={height} />;
  }
  
  // Use static map component for mobile or when forced
  if (useStaticMaps) {
    return (
      <StaticMapDisplay
        location={location}
        currentLocation={userLocation}
        width={width}
        height={height}
        zoom={zoom}
        showWhenEmpty={showWhenEmpty}
        showRefresh={!effectiveDraggable} // Show refresh button when not in edit mode
      />
    );
  }

  // Render function for Wrapper component
  const render = (status) => {
    switch (status) {
      case Status.LOADING:
        return <MapLoading width={width} height={height} />;
      case Status.FAILURE:
        return <MapError width={width} height={height} error="Failed to load Google Maps" />;
      case Status.SUCCESS:
        return (
          <GoogleMap
            location={location}
            currentLocation={userLocation}
            draggable={effectiveDraggable}
            onLocationChange={handleLocationChange}
            onAddressChange={onAddressChange}
            width={width}
            height={height}
            zoom={zoom}
            apiKey={apiKey}
          />
        );
      default:
        return <MapLoading width={width} height={height} />;
    }
  };

  // Don't render if no API key
  if (!apiKey) {
    return (
      <Box sx={{ width, height }}>
        <Alert severity="info">
          <Typography variant="body2">
            Google Maps API key not configured
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width, height }}>
      <Wrapper 
        apiKey={apiKey} 
        render={render} 
        libraries={['marker']}
      />
      {locationError && effectiveDraggable && showWhenEmpty && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          Location error: {locationError}
        </Typography>
      )}
    </Box>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(MapLocationDisplay, (prevProps, nextProps) => {
  // Only re-render if relevant props actually changed
  const prevLoc = prevProps.location;
  const nextLoc = nextProps.location;
  const prevCurLoc = prevProps.currentLocation;
  const nextCurLoc = nextProps.currentLocation;
  
  return (
    prevProps.draggable === nextProps.draggable &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.apiKey === nextProps.apiKey &&
    prevProps.showWhenEmpty === nextProps.showWhenEmpty &&
    prevProps.forceStatic === nextProps.forceStatic &&
    // Deep compare location objects
    (prevLoc === nextLoc || (
      prevLoc?.coordinates?.lat === nextLoc?.coordinates?.lat &&
      prevLoc?.coordinates?.lng === nextLoc?.coordinates?.lng
    )) &&
    (prevCurLoc === nextCurLoc || (
      prevCurLoc?.coordinates?.lat === nextCurLoc?.coordinates?.lat &&
      prevCurLoc?.coordinates?.lng === nextCurLoc?.coordinates?.lng
    ))
  );
});